import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { MatSnackBar, MatSnackBarConfig } from "@angular/material/snack-bar";
import { BehaviorSubject, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { API_URL } from "../../environments/environment";
import { sortByStringWithEmptyAtEnd } from "../shared/helpers/helper-functions";
import {
  Area,
  Attribute,
  AttributeType,
  Branch,
  Customer,
  CustomerSearchAPI,
  Department,
  FormType,
  Language,
  OrderType,
  Priority,
  Product,
  ProductCategory,
  QuestionnaireField,
  Status,
  StatusIcon,
} from "./master-data.model";

@Injectable({
  providedIn: "root",
})
export class MasterDataService {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);

  languages: Language[] = [];
  areas: Area[] = [];
  priorities: Priority[] = [];
  statuses: Status[] = [];
  types: OrderType[] = [];
  productCategories: ProductCategory[];
  product: Product[];
  private departments: Department[] = [];

  private productCategoriesMapBS$ = new BehaviorSubject<Map<string, string>>(
    new Map<string, string>(),
  );
  productCategoriesMap$ = this.productCategoriesMapBS$.asObservable();

  clearMasterData() {
    this.areas = [];
    this.priorities = [];
    this.statuses = [];
    this.types = [];
    this.productCategories = [];
    this.departments = [];
  }

  getLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(API_URL + "languages");
  }

  getAreas(): Observable<Area[]> {
    return this.http.get<Area[]>(API_URL + "area");
  }

  updateArea(area): Observable<HttpResponse<Area>> {
    console.log("Update area", area);
    return this.http.post<Area>(API_URL + "area", area, {
      observe: "response",
    });
  }

  deleteArea(area) {
    console.log("Delete area", area);
  }

  getUserAttributes(): Observable<Attribute[]> {
    return this.http.get<Attribute[]>(
      API_URL + "attribute/type/" + AttributeType.USER,
    );
  }

  getInstallationAttribute(): Observable<Attribute[]> {
    return this.http.get<Attribute[]>(
      API_URL + "attribute/type/" + AttributeType.INSTALLATION,
    );
  }

  updateAttribute(attr: Attribute): Observable<HttpResponse<Attribute>> {
    console.log("Update attribute", attr);
    return this.http.post<Attribute>(API_URL + "attribute", attr, {
      observe: "response",
    });
  }

  getBranches(): Observable<Branch[]> {
    return this.http.get<Branch[]>(API_URL + "branch");
  }

  getStatuses(): Observable<Status[]> {
    return this.http.get<Status[]>(API_URL + "status");
  }

  getStatusIcons(): Observable<StatusIcon[]> {
    return this.http.get<StatusIcon[]>(API_URL + "icon");
  }

  updateStatus(status): Observable<HttpResponse<Status>> {
    console.log("Update status", status);
    return this.http.post<Status>(API_URL + "status", status, {
      observe: "response",
    });
  }

  deleteStatus(status: Status) {
    console.log("Delete status", status);
    return this.http.delete<Status>(API_URL + "status/" + status.uuid, {
      observe: "response",
    });
  }

  getTypes(): Observable<OrderType[]> {
    return this.http.get<OrderType[]>(API_URL + "type");
  }

  updateType(type): Observable<HttpResponse<OrderType>> {
    console.log("Update type", type);
    return this.http.post<OrderType>(API_URL + "type", type, {
      observe: "response",
    });
  }

  deleteType(type) {
    console.log("Delete type", type);
  }

  getPriorities(): Observable<Priority[]> {
    return this.http.get<Priority[]>(API_URL + "priority");
  }

  updatePriority(priority): Observable<HttpResponse<Priority>> {
    console.log("Update priority", priority);
    return this.http.post<Priority>(API_URL + "priority", priority, {
      observe: "response",
    });
  }

  deletePriority(priority) {
    console.log("Delete priority", priority);
  }

  getFormTypes(): Observable<FormType[]> {
    return this.http.get<FormType[]>(API_URL + "formtype");
  }

  updateFormType(ftype): Observable<HttpResponse<FormType>> {
    console.log("Update form type", ftype);
    return this.http.post<FormType>(API_URL + "formtype", ftype, {
      observe: "response",
    });
  }

  getDepartments(): Observable<Department[]> {
    return this.http.get<Department[]>(API_URL + "department");
  }

  getProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(API_URL + "product");
  }

  getProductCategories(): Observable<ProductCategory[]> {
    return this.http.get<ProductCategory[]>(API_URL + "productcategory").pipe(
      tap((pcs) => {
        const prodCateg = sortByStringWithEmptyAtEnd(pcs, (item) => item.name);
        //pcs.sort((a, b) => a.name.localeCompare(b.name));
        const pcsMap = new Map<string, string>();
        prodCateg.forEach((pc) => pcsMap.set(pc.uuid, pc.name));
        this.productCategoriesMapBS$.next(pcsMap);
      }),
    );
  }

  updateProductCategory(category): Observable<HttpResponse<ProductCategory>> {
    console.log("Update category", category);
    return this.http.post<ProductCategory>(
      API_URL + "productcategory",
      category,
      {
        observe: "response",
      },
    );
  }

  handleResponse(response: HttpResponse<unknown>) {
    if (response.status === 200 || response.status === 201) {
      console.log("Save OK!");
      this.snackBar.open("SUCCESS", "", {
        duration: 2000,
        panelClass: "app-snackbar-success",
      });
    } else {
      this.snackBar.open("ERROR", "", {
        duration: 2000,
        panelClass: "app-snackbar-alert",
      });
      console.log("Save ERROR!");
    }
  }

  getCustomers(): Observable<Customer[]> {
    return this.http
      .get<CustomerSearchAPI>(API_URL + "customer")
      .pipe(map((page) => page.content));
  }

  savingErrorMessage() {
    console.log("Save ERROR!");
    const config = new MatSnackBarConfig();
    config.panelClass = ["app-snack-panel"];
    config.duration = 2500;
    config.verticalPosition = "top";
    this.snackBar.open(
      "Data save error",
      "Problem with saving to database",
      config,
    );
  }

  getQuestionnaireFieldsForStatus(
    customerUuid: string,
    statusUuid: string,
  ): Observable<QuestionnaireField[]> {
    return this.http.get<QuestionnaireField[]>(API_URL + "questionnaire", {
      params: new HttpParams()
        .set("customerUuid", customerUuid)
        .set("statusUuid", statusUuid),
    });
  }

  deleteQuestionnaireField(uuid: string) {
    this.http.delete(API_URL + "questionnaire/" + uuid).subscribe();
  }

  saveQuestionnaireField(field: QuestionnaireField) {
    this.http
      .post<QuestionnaireField>(API_URL + "questionnaire/", field)
      .subscribe();
  }
}
